import { createFeature, createReducer, on } from '@ngrx/store';
import { ConnectorsActions } from './connectors.actions';
import { ConnectorProvider, ConnectorView } from './connectors.models';

export const connectorsFeatureKey = 'connectors';

export interface ConnectorsState {
  connectors: ConnectorView[];
  isLoading: boolean;
  loaded: boolean;
  error: string | null;
  /** Which provider has a disconnect in flight, so only that row shows a pending state. */
  disconnecting: ConnectorProvider | null;
  /**
   * Whether a load ever succeeded (`done`) or the first one failed (`failed`). Unlike `loaded`, a
   * later failed refresh does not undo `done`: the last list stays usable (SB-6520 KD-19, spec 3.7).
   */
  firstLoad: 'pending' | 'done' | 'failed';
}

export const initialConnectorsState: ConnectorsState = {
  connectors: [],
  isLoading: false,
  loaded: false,
  error: null,
  disconnecting: null,
  firstLoad: 'pending',
};

export const connectorsFeature = createFeature({
  name: connectorsFeatureKey,
  reducer: createReducer(
    initialConnectorsState,
    on(
      ConnectorsActions.loadConnectors,
      ConnectorsActions.reloadConnectors,
      (state) => ({
        ...state,
        isLoading: true,
        error: null,
      }),
    ),
    // Only after a success: the list in the store is the answer, so nothing is loading any more.
    on(ConnectorsActions.loadConnectorsSkipped, (state) => ({
      ...state,
      isLoading: false,
    })),
    // Sorted once here so the modal and the "+" menu list in the same order: alphabetical by name,
    // as the spec asks (A2, B1). The API returns catalog order, which is an operator's choice.
    on(ConnectorsActions.loadConnectorsSuccess, (state, { connectors }) => ({
      ...state,
      connectors: [...connectors].sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      ),
      isLoading: false,
      loaded: true,
      firstLoad: 'done' as const,
    })),
    // Not `loaded`: a failed fetch is not a list, and marking it loaded would hide the Connectors row
    // until the next explicit refresh. Leaving it false lets the next composer mount try again.
    on(ConnectorsActions.loadConnectorsFailure, (state, { error }) => ({
      ...state,
      isLoading: false,
      loaded: false,
      error,
      firstLoad:
        state.firstLoad === 'done' ? state.firstLoad : ('failed' as const),
    })),
    on(ConnectorsActions.disconnectProvider, (state, { provider }) => ({
      ...state,
      disconnecting: provider,
    })),
    // The list is reloaded rather than patched in place: disconnecting is rare, and re-reading keeps
    // one source of truth for a row's status instead of two ways for it to be right.
    on(ConnectorsActions.disconnectProviderSuccess, (state) => ({
      ...state,
      disconnecting: null,
    })),
    on(ConnectorsActions.disconnectProviderFailure, (state) => ({
      ...state,
      disconnecting: null,
    })),
  ),
});
